import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Link } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import MainLayout from "../layout/MainLayout";
import { LoadingSpinner } from "@/components";
import { useAuth } from "@/modules/auth";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const CertificatesPage = lazy(() => import("@/pages/CertificatesPage"));
const SignaturesPage = lazy(() => import("@/pages/SignaturesPage"));
const EmailVerificationPage = lazy(
  () => import("@/pages/EmailVerificationPage")
);
const TwoFactorSetupPage = lazy(() =>
  import("@/pages/TwoFactorSetupPage").then((module) => ({
    default: module.TwoFactorSetupPage,
  }))
);
const TwoFactorManagementPage = lazy(() =>
  import("@/pages/TwoFactorManagementPage").then((module) => ({
    default: module.TwoFactorManagementPage,
  }))
);
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ForgotPasswordConfirmationPage = lazy(
  () => import("@/pages/ForgotPasswordConfirmationPage")
);
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const ResetPasswordSuccessPage = lazy(
  () => import("@/pages/ResetPasswordSuccessPage")
);
const ContactPage = lazy(() => import("@/pages/ContacsPage"));
const DossierList = lazy(
  () => import("@/modules/dossiers/components/DossierList")
);
const DossierDetail = lazy(
  () => import("@/modules/dossiers/components/DossierDetail")
);
const DocumentList = lazy(
  () => import("@/modules/documents/components/DocumentList")
);
const OrganizationSettings = lazy(() =>
  import("@/modules/organization/components/OrganizationSettings").then(
    (module) => ({
      default: module.OrganizationSettings,
    })
  )
);
const UserManagement = lazy(() =>
  import("@/modules/organization/components/UserManagement").then((module) => ({
    default: module.UserManagement,
  }))
);
const CompaniesManagement = lazy(() =>
  import("@/modules/companies/components/CompaniesManagement").then((module) => ({
    default: module.CompaniesManagement,
  }))
);
const PlanManagement = lazy(() =>
  import("@/modules/plans/components/PlanManagement").then((module) => ({
    default: module.PlanManagement,
  }))
);
const SystemSigningPage = lazy(() =>
  import("@/pages/SystemSigningPage").then((module) => ({
    default: module.SystemSigningPage,
  }))
);
const ExternalSignPage = lazy(() =>
  import("@/modules/public/ExternalSignPage").then((module) => ({
    default: module.ExternalSignPage,
  }))
);
const ProfilePage = lazy(
  () => import("@/modules/users/components/ProfilePage")
);

const RouteFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user, twoFa } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Ruta pública - Login */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <LoginPage />
            )
          }
        />

        {/* Ruta pública - Registro con código de invitación */}
        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <LoginPage />
            )
          }
        />

        <Route path="/verify-email" element={<EmailVerificationPage />} />

        {/* Rutas públicas - Password Reset */}
        <Route
          path="/forgot-password"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <ForgotPasswordPage />
            )
          }
        />
        <Route
          path="/forgot-password/confirmation"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <ForgotPasswordConfirmationPage />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <ResetPasswordPage />
            )
          }
        />
        <Route
          path="/reset-password/success"
          element={
            isAuthenticated ? (
              <Navigate to={twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard"} replace />
            ) : (
              <ResetPasswordSuccessPage />
            )
          }
        />

        {/* Ruta semi-protegida - Setup 2FA (requiere token temporal) */}
        <Route
          path="/setup-2fa"
          element={
            !isAuthenticated ? (
              <Navigate to="/login" replace />
            ) : twoFa?.requiresSetup ? (
              <TwoFactorSetupPage />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />

        {/* Ruta pública - Firma Externa */}
        <Route path="/sign/:token" element={<ExternalSignPage />} />

        {/* Rutas protegidas */}
        <Route
          path="/settings/2fa"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TwoFactorManagementPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contacts"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ContactPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificates"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.profile?.profileName === "SUPER_ADMIN" ? (
                  <CertificatesPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DocumentList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/signatures"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SignaturesPage />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DossierList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dossiers/:id"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DossierDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.role === "ADMIN" ? (
                  <OrganizationSettings />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/organization/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.role === "ADMIN" ? (
                  <UserManagement />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/companies"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.role === "SUPER_ADMIN" ? (
                  <CompaniesManagement />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/plans"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.role === "SUPER_ADMIN" ? (
                  <PlanManagement />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/system-signing"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user?.role === "SUPER_ADMIN" ? (
                  <SystemSigningPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ProfilePage />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirección por defecto */}
        <Route
          path="/"
          element={
            <Navigate to={isAuthenticated ? (twoFa?.requiresSetup ? "/setup-2fa" : "/dashboard") : "/login"} replace />
          }
        />

        {/* Página 404 */}
        <Route
          path="*"
          element={
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
              <div className="card-legal p-8 text-center">
                <h1 className="text-4xl font-bold text-navy mb-4">404</h1>
                <p className="text-slate-600 mb-4">Página no encontrada</p>
                <Link
                  to="/dashboard"
                  className="text-primary hover:text-primary/80 transition-colors duration-200 font-semibold"
                >
                  Volver al Dashboard
                </Link>
              </div>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
